import { FormikProps } from "formik"
import { useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { ErrorDisplay, NoDataError, SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { NoDataFallback, QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import {
    SERVICE_CENTER_ADMIN_ACCOUNT_ADMINISTRATORS_ROUTE,
    SERVICE_CENTER_ADMIN_ROUTE,
} from "../../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { selectUserId } from "../../../../../login/loginSelectors"
import { UserType } from "../../../../../login/loginTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { AdministratorForm } from "../../../../../users/administrators/components/AdministratorForm"
import { AdministratorFormValues } from "../../../../../users/administrators/administratorFormTypes"
import {
    toAdministratorFormValues,
    toUpdateAdministratorFields,
} from "../../../../../users/administrators/utils/administratorBody"
import { GetAdminDetailsResponse, getAdminDetails, updateAdmin, UserQuery } from "../../../../../users/userApi"
import { UpdateServiceCenterAdmin, UserActionType } from "../../../../../users/userTypes"
import { ServiceCenterAdminsQuery } from "../adminsApi"

function selectServiceCenterAdmin({ admin }: GetAdminDetailsResponse) {
    if (admin.type !== UserType.SERVICE_CENTER) throw new NoDataError()

    return {
        userId: admin.user.id,
        initialValues: toAdministratorFormValues(admin),
        history: admin.history,
        serviceCenterDetails: admin.serviceCenterDetails,
    }
}

export function EditServiceCenterAdminPage() {
    const navigate = useNavigate()
    const { userId: userIdParam } = useParams<{ userId?: string }>()
    const userId = Number(userIdParam)
    const isValidUserId = Number.isFinite(userId)

    const currentUserId = useAppSelector(selectUserId)

    const formRef = useRef<FormikProps<AdministratorFormValues>>(null)

    const adminResult = useApiQuery(getAdminDetails, {
        queryName: UserQuery.ADMIN_DETAILS,
        dependencies: { userId },
        enabled: isValidUserId,
        select: selectServiceCenterAdmin,
        errorDisplay: ErrorDisplay.NONE,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateAdmin, isLoading: isUpdating } = useApiMutation(updateAdmin, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, _, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterAdminsQuery.ADMINS_FOR_SERVICE_CENTER],
            })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(UserQuery.ADMIN_DETAILS, { userId: params.userId }),
            })

            navigate(SERVICE_CENTER_ADMIN_ACCOUNT_ADMINISTRATORS_ROUTE)
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    async function handleSubmit(values: AdministratorFormValues) {
        if (!adminResult.data) return

        const admin: UpdateServiceCenterAdmin = {
            type: UserType.SERVICE_CENTER,
            ...toUpdateAdministratorFields(values),
            serviceCenterDetails: adminResult.data.serviceCenterDetails,
        }

        await callUpdateAdmin({
            userId: adminResult.data.userId,
            admin,
            actions: values.resendInvitation ? [{ type: UserActionType.SEND_EMAIL_INVITATION }] : undefined,
        })
    }

    return (
        <>
            <LocalizedStrict id="administrators-edit-page-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SERVICE_CENTER_ADMIN_ACCOUNT_EDIT_ADMINS_ROUTE}
                    title="Edit administrator"
                    rightElement={
                        <LocalizedStrict id="administrators-edit-page-save-button">
                            <StandardLoadingButton
                                type="submit"
                                onClick={handleSaveClick}
                                loading={isUpdating}
                                disabled={!adminResult.data}
                            >
                                Save
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="service-center-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-nav-item-account">
                            <Breadcrumb>Account</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-account-administrators">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ACCOUNT_ADMINISTRATORS_ROUTE}>
                                Administrators
                            </BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="administrators-edit-breadcrumb">
                            <Breadcrumb>Edit administrator</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary dependencies={{ userId }}>
                {!isValidUserId && <NoDataFallback />}
                {isValidUserId &&
                    (adminResult.data ? (
                        <AdministratorForm
                            ref={formRef}
                            adminType={UserType.SERVICE_CENTER}
                            isEditMode
                            isOwnProfile={adminResult.data.userId === currentUserId}
                            initialValues={adminResult.data.initialValues}
                            history={adminResult.data.history}
                            onSubmit={handleSubmit}
                        />
                    ) : (
                        <LoadingSpinner />
                    ))}
            </QueryErrorBoundary>
        </>
    )
}
